import React, { useEffect, useState, useCallback } from 'react';
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';

import StatusBadge from './StatusBadge';
import ActionButtons from './ActionsButtons';
import NuevoGrupoModal from './NuevoGrupoModal';
import { getGroupsPaginated } from '../../api/groups';
import Pagination from '../../components/Pagination.tsx';

// Columnas dinámicas y responsivas usando minmax y fr
// Eliminadas: Campus y Tipo para reducir solapamiento
// Total: 11 columnas (antes eran 13)
// Optimizado para diferentes niveles de zoom y tamaños de pantalla
const gridCols = [
  'minmax(65px, 0.8fr)',    // ID Grupo
  'minmax(110px, 1.6fr)',   // Materia
  'minmax(100px, 1.4fr)',   // Asesor
  'minmax(100px, 1.1fr)',   // Sección
  'minmax(60px, 0.65fr)',   // Ciclo
  'minmax(45px, 0.55fr)',   // Cupo
  'minmax(85px, 1fr)',      // Modalidad
  'minmax(85px, 1fr)',      // Horario
  'minmax(100px, 1.2fr)',   // Programa
  'minmax(80px, 0.95fr)',   // Estado
  'minmax(80px, 0.9fr)'    // Acciones
].join(' ');

const GroupsTable = ({ onLoadingChange = () => {}, query = '', campusFilter = '', campusIdFilter = null, cicloIdFilter = null }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  // Paginación local controlada, pero obteniendo solo una página del backend
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // El backend ya devuelve una sola página de resultados
  const paginated = groups;

  // Reiniciar a la primera página cuando cambian los filtros de búsqueda
  useEffect(() => { setPage(1); }, [query, campusIdFilter, cicloIdFilter]);

  // Si al cambiar los datos la página actual queda fuera de rango,
  // ajustar a la última página disponible (por ejemplo, después de eliminar).
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);
  // Eliminar goToPage, usaremos Pagination global
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view' | 'edit'
  const [selectedGroup, setSelectedGroup] = useState(null);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      onLoadingChange(true);
      console.log('[GroupsTable] Iniciando carga de grupos...');
      const params = {
        page,
        page_size: pageSize,
      };
      if (campusIdFilter) {
        params.id_campus = campusIdFilter;
      }
      if (cicloIdFilter) {
        params.id_ciclo_escolar = cicloIdFilter;
      }
      if (query && query.trim()) {
        params.search = query.trim();
      }

      console.log('[GroupsTable] Usando getGroupsPaginated con parámetros:', params);
      const { results, count } = await getGroupsPaginated(params);
      console.log('[GroupsTable] Grupos recibidos:', count, results);
      setGroups(results || []);
      setTotal(typeof count === 'number' ? count : (results || []).length);
    } catch (e) {
      console.error('[GroupsTable] ❌ Error cargando grupos:', e);
      setGroups([]);
      setTotal(0);
    } finally {
      setLoading(false);
      onLoadingChange(false);
    }
  }, [onLoadingChange, campusIdFilter, cicloIdFilter, page, query]);

  useEffect(() => {
    fetchGroups();
    const handler = () => fetchGroups();
    window.addEventListener('groups:updated', handler);
    return () => window.removeEventListener('groups:updated', handler);
  }, [fetchGroups]);

  const handleView = (group) => {
    setSelectedGroup(group);
    setModalMode('view');
    setModalOpen(true);
  };
  const handleEdit = (group) => {
    setSelectedGroup(group);
    setModalMode('edit');
    setModalOpen(true);
  };
  const handleDelete = async (group) => {
    if (!window.confirm(`¿Seguro que deseas eliminar el grupo ${group.codigo_grupo}?`)) return;
    try {
      const { deleteGroup } = await import('../../api/groups');
      await deleteGroup(group.id);  // Usa id_grupo (PK) para eliminar
      setModalOpen(false);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Grupo eliminado correctamente' } }));
    } catch (err) {
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'Error al eliminar: ' + err.message } }));
    }
  };

  const Header = () => (
    <header className={styles.tableHeader}>
      <div className={styles.tableTitle}>
        <div className={styles.tableTitleIcon}>
          <div className={styles.iconContainerOrange}><span>👨‍👦‍👦</span></div>
        </div>
        <h2 className={styles.tableTitleText}>Grupos</h2>
      </div>
      {/* Paginación superior eliminada */}
    </header>
  );

  return (
    <>
      <section className={styles.tableContainer} role="region" aria-label="Tabla de grupos">
        <Header />

        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Cargando grupos...</div>
        ) : (
          <>
            <div className={styles.tableHeaderRow} style={{ gridTemplateColumns: gridCols }}>
              <span className={styles.columnHeader}>ID Grupo</span>
              <span className={styles.columnHeader}>Materia</span>
              <span className={styles.columnHeader}>Asesor</span>
              <div className={styles.columnHeader}>
                <span>Sección</span>
                <img
                  src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/ac3418739771e5c9f651d8b5020bd45222ab4f63?placeholderIfAbsent=true"
                  alt="Sort"
                  className={styles.sortIcon}
                />
              </div>
              <span className={styles.columnHeader}>Ciclo</span>
              <span className={styles.columnHeader}>Cupo</span>
              <span className={styles.columnHeader}>Modalidad</span>
              <span className={styles.columnHeader}>Horario</span>
              <span className={styles.columnHeader}>Programa</span>
              <span className={styles.columnHeader}>Estado</span>
              <span className={styles.columnHeaderWide}>Acciones</span>
            </div>

            <div className={styles.tableBody}>
              {paginated.length === 0 && (
                <div className={styles.tableRow}>
                  <span className={styles.cellData}>Sin resultados.</span>
                  <span className={styles.cellData}>—</span>
                  <span className={styles.cellData}>—</span>
                  <span className={styles.cellData}>—</span>
                  <span className={styles.cellData}>—</span>
                  <span className={styles.cellData}>—</span>
                  <span className={styles.cellData}>—</span>
                  <span className={styles.cellData}>—</span>
                  <span className={styles.cellData}>—</span>
                  <div className={styles.statusCell}><span>—</span></div>
                  <div className={styles.cellData}>—</div>
                </div>
              )}
              {paginated.map((g) => (
                <div key={g.id} className={styles.tableRow} style={{ gridTemplateColumns: gridCols }}>
                  <span className={styles.cellData}>{g.codigo_grupo || '—'}</span>
                  <span className={styles.cellData}>{g.materia || '—'}</span>
                  <span className={styles.cellData}>{g.asesor || '—'}</span>
                  <span className={styles.cellData}>{g.seccion || '—'}</span>
                  <span className={styles.cellData}>{g.ciclo || '—'}</span>
                  <span className={styles.cellData}>{g.cupo || '—'}</span>
                  <span className={styles.cellData}>{g.modalidad || '—'}</span>
                  <span className={styles.cellData}>{g.horario || '—'}</span>
                  <span className={styles.cellData}>{g.programa || '—'}</span>
                  <div className={styles.statusCell}><StatusBadge status={g.estadoBadge} /></div>
                  <ActionButtons group={g} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
                </div>
              ))}
            </div>

            {/* Paginación global con indicador de registros */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
              <span style={{ fontSize: '14px', color: '#898d8d' }}>
                Mostrando {paginated.length > 0 ? ((page - 1) * pageSize) + 1 : 0}–{Math.min(page * pageSize, total)} de {total} grupos
              </span>
              {totalPages > 1 && (
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              )}
            </div>
          </>
        )}
      </section>
      <NuevoGrupoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        group={selectedGroup}
        mode={modalMode}
        onCreated={() => {
          setModalOpen(false);
          try {
            const msg = modalMode === 'edit'
              ? 'Grupo actualizado correctamente'
              : 'Grupo creado correctamente';
            window.alert(msg);
          } catch (_) {
            // Ignorar en entornos sin window (tests, SSR)
          }
        }}
      />
    </>
  );
};

export default GroupsTable;
